import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/auth-provider";
import { LeaseAgreement } from "@/types/types";
import { getLandlordLeases } from "@/lib/lease-service";
import { formatNaira } from "@/lib/utils";
import { LeaseSigningDialog } from "@/components/leases/lease-signing-dialog";
import { getLeaseStatusVariant, formatLeaseStatus } from "@/lib/lease-utils";

export default function LandlordLeasesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leases, setLeases] = useState<LeaseAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLease, setSelectedLease] = useState<LeaseAgreement | null>(
    null
  );
  const [showSigningDialog, setShowSigningDialog] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    loadLeases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadLeases() {
    if (!user?.id) return;

    try {
      setLoading(true);
      const result = await getLandlordLeases(user.id, 100); // Get up to 100 leases
      setLeases(result.data);
    } catch (error) {
      console.error("Error loading leases:", error);
      toast.error("Failed to load leases");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Lease Agreements</h1>
        <p className="text-muted-foreground">
          Manage lease agreements for your properties
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Leases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {leases.filter((l) => l.status === "active").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Pending Signatures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {leases.filter((l) => l.status === "pending_signatures").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {
                leases.filter((l) => {
                  const daysUntilExpiry = Math.ceil(
                    (new Date(l.endDate).getTime() - Date.now()) /
                      (1000 * 60 * 60 * 24)
                  );
                  return (
                    l.status === "active" &&
                    daysUntilExpiry <= 60 &&
                    daysUntilExpiry > 0
                  );
                }).length
              }
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Leases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leases.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Leases Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lease Agreements</CardTitle>
          <CardDescription>{leases.length} leases found</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading leases...</div>
          ) : leases.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No leases found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Rent</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leases.map((lease) => (
                  <TableRow key={lease.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {lease.propertyTitle ||
                            `Property ${lease.propertyId.slice(0, 8)}`}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">
                        {lease.tenantName ||
                          `Tenant ${lease.tenantId.slice(0, 8)}`}
                      </p>
                    </TableCell>
                    <TableCell>{formatNaira(lease.monthlyRent)}</TableCell>
                    <TableCell>
                      {new Date(lease.startDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(lease.endDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getLeaseStatusVariant(lease.status)}>
                        {formatLeaseStatus(lease.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`/landlord/leases/${lease.id}`)
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {lease.status === "pending_signatures" &&
                          !lease.landlordSignature && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedLease(lease);
                                setShowSigningDialog(true);
                              }}
                            >
                              Sign
                            </Button>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Signing Dialog */}
      {selectedLease && (
        <LeaseSigningDialog
          open={showSigningDialog}
          onOpenChange={(open) => {
            setShowSigningDialog(open);
            if (!open) {
              setSelectedLease(null);
              loadLeases();
            }
          }}
          lease={selectedLease}
        />
      )}
    </div>
  );
}
